<template>
  <div class="sitemap-page max-w-(--breakpoint-xl) mx-auto py-10">
    <h1 class="text-3xl font-bold mb-8">{{ t('sitemap.title') }}</h1>
    <div class="mb-10">
      <h2 class="text-xl font-semibold mb-4">
        {{ t('sitemap.sections.categories') }}
      </h2>
      <ul
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
      >
        <li v-for="item in categories" :key="item.id">
          <a
            :href="`${baseUrl}/${locale}/c/${item.slug}/`"
            class="hover:underline"
            >{{ item.title }}</a
          >
        </li>
      </ul>
    </div>
    <div class="mb-10">
      <h2 class="text-xl font-semibold mb-4">
        {{ t('sitemap.sections.cities') }}
      </h2>
      <ul
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
      >
        <li v-for="item in cities" :key="item.id">
          <a
            :href="`${baseUrl}/${locale}/${item.slug}/`"
            class="hover:underline"
            >{{ item.title }}</a
          >
        </li>
      </ul>
    </div>
    <div class="mb-10">
      <h2 class="text-xl font-semibold mb-4">
        {{ t('sitemap.sections.cityCategories') }}
      </h2>
      <ul
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
      >
        <li v-for="item in cityCategories" :key="item.id">
          <a
            :href="`${baseUrl}/${locale}/${item.citySlug}/${item.categorySlug}/`"
            class="hover:underline"
            >{{ item.title }}</a
          >
        </li>
      </ul>
    </div>
    <div class="mb-10">
      <h2 class="text-xl font-semibold mb-4">
        {{ t('sitemap.sections.generalTransactionalPages') }}
      </h2>
      <ul
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
      >
        <li v-for="item in tags" :key="item.id">
          <a
            :href="`${baseUrl}/${locale}/t/${item.slug}/`"
            class="hover:underline"
            >{{ item.title }}</a
          >
        </li>
      </ul>
    </div>
    <div class="mb-10">
      <h2 class="text-xl font-semibold mb-4">
        {{ t('sitemap.sections.teamEvents') }}
      </h2>
      <ul
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
      >
        <li v-for="item in filteredTeamevents" :key="item.id">
          <a :href="getTeameventHref(item.slug)" class="hover:underline">{{
            item.title
          }}</a>
        </li>
      </ul>
    </div>
    <div class="mb-10">
      <h2 class="text-xl font-semibold mb-4">
        {{ t('sitemap.sections.kits') }}
      </h2>
      <ul
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2"
      >
        <li v-for="item in kits" :key="item.id">
          <a
            :href="`${baseUrl}/${locale}/kits/${item.slug}/`"
            class="hover:underline"
            >{{ item.title }}</a
          >
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRuntimeConfig } from '#app'
import { computed } from 'vue'

const { t, locale } = useI18n()
const config = useRuntimeConfig()
const baseUrl = config.public.baseUrl?.replace(/\/$/, '') || ''

interface SitemapItem {
  id: string | number
  title: string
  slug?: string
  citySlug?: string
  categorySlug?: string
}

async function fetchAllStrapi(urlBase: string, apiKey: string): Promise<any[]> {
  const headers = { Authorization: `Bearer ${apiKey}` }
  let page = 1
  const allItems: any[] = []

  while (true) {
    const url = `${urlBase}&pagination[page]=${page}`

    const res: any = await $fetch(url, { headers })
    const data: any[] = res && res.data ? res.data : []
    allItems.push(...data)

    const metaPagination =
      res && res.meta && res.meta.pagination ? res.meta.pagination : null

    let currentPage = page
    let pageCount = page
    if (metaPagination) {
      if (typeof metaPagination.page === 'number') {
        currentPage = metaPagination.page
      }
      if (typeof metaPagination.pageCount === 'number') {
        pageCount = metaPagination.pageCount
      }
    }

    if (currentPage >= pageCount || data.length === 0) break
    page += 1
  }
  return allItems
}

function useSitemapGroup(
  type: string,
  fields: string,
  mapFn: (item: any) => SitemapItem
) {
  const config = useRuntimeConfig()
  const strapiBaseUrl = config.public.strapiApiBaseURL
  const strapiApiKey = config.public.strapiApiKey

  const { data: raw } = useAsyncData(
    () => `sitemap-${type}-${locale.value}`,
    async () => {
      const urlBase = `${strapiBaseUrl}event-list-pages?filters[type][$eq]=${type}&fields=${fields}&locale=${locale.value}`
      return await fetchAllStrapi(urlBase, strapiApiKey)
    },
    { watch: [locale] }
  )
  return computed<SitemapItem[]>(() => (raw.value || []).map(mapFn))
}

function useKitsSitemapGroup() {
  const config = useRuntimeConfig()
  const strapiBaseUrl = config.public.strapiApiBaseURL
  const strapiApiKey = config.public.strapiApiKey

  const { data: raw } = useAsyncData(
    () => `sitemap-kits-${locale.value}`,
    async () => {
      const urlBase = `${strapiBaseUrl}kits-list-pages?fields=title,slug&locale=${locale.value}`
      return await fetchAllStrapi(urlBase, strapiApiKey)
    },
    { watch: [locale] }
  )
  return computed<SitemapItem[]>(() =>
    (raw.value || []).map((item: any) => ({
      id: item.id,
      title: item.title,
      slug: item.slug?.replace(/_[a-z]{2}-[a-z]{2}$/i, '') || item.slug,
    }))
  )
}

const categories = useSitemapGroup(
  'category',
  'categorySlug,locale,title',
  (item: any) => ({ id: item.id, title: item.title, slug: item.categorySlug })
)
const cities = useSitemapGroup(
  'city',
  'citySlug,locale,title',
  (item: any) => ({ id: item.id, title: item.title, slug: item.citySlug })
)
const cityCategories = useSitemapGroup(
  'city-category',
  'citySlug,categorySlug,locale,title',
  (item: any) => ({
    id: item.id,
    title: item.title,
    citySlug: item.citySlug,
    categorySlug: item.categorySlug,
  })
)
const tags = useSitemapGroup('general', 'slug,locale,title', (item: any) => ({
  id: item.id,
  title: item.title,
  slug: item.slug,
}))
const teamevents = useSitemapGroup(
  'teamevent',
  'slug,locale,citySlug,title',
  (item: any) => ({ id: item.id, title: item.title, slug: item.slug })
)

const filteredTeamevents = computed(() =>
  teamevents.value.filter((item) => !!item.slug)
)

const kits = useKitsSitemapGroup()

function getTeameventHref(slug: string | undefined) {
  if (!slug) return ''
  const currentLocale = locale.value
  const city = slug.replace(/^teamevent-|^teambuilding-/, '')
  return `${baseUrl}/${currentLocale}/teamevent/${city}/`
}
</script>
